// D1 · capture-from-trace. A self-contained "＋ add to golden set" affordance
// for a conversation turn in the Inspector: pick a dataset, edit the expected
// answer (pre-filled with the turn's actual answer), and link the turn's
// Langfuse trace as a golden-set item (source = "trace"). All validation /
// payload logic lives in the tested `buildTraceCapture` unit; this component
// is the thin UI shell around it.
import React, { useEffect, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Button, TextArea, Select, Modal } from "../../../components/ui";
import { observabilityApi } from "../../../api/client";
import { buildTraceCapture, normalizeItems } from "./datasetItemFormat";

export default function AddToGoldenSetButton({ traceId, observationId = null, defaultExpected = "" }) {
  const [open, setOpen] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState("");
  const [expected, setExpected] = useState(defaultExpected || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      const res = await observabilityApi.listDatasets();
      const rows = normalizeItems(res);
      if (!alive) return;
      setDatasets(rows);
      if (rows[0]) setDatasetId(rows[0].id);
    })();
    return () => { alive = false; };
  }, [open]);

  async function save() {
    setError(null);
    const { payload, error: err } = buildTraceCapture({
      datasetId, traceId, observationId, expectedOutput: expected,
    });
    if (err) { setError(err); return; }
    setBusy(true);
    try {
      await observabilityApi.addTraceToDataset(
        payload.datasetId, payload.traceId, payload.observationId, payload.expectedOutput,
      );
      setDone(true);
      setOpen(false);
    } catch (e) {
      setError(e?.message || "Failed to add to golden set");
    } finally {
      setBusy(false);
    }
  }

  const close = () => setOpen(false);

  const linkStyle = {
    background: "none", border: "none", cursor: "pointer",
    color: COLORS.accentText || COLORS.accent || "#2563eb",
    fontSize: 10, fontWeight: 600,
  };

  return (
    <>
      <button
        onClick={() => { setDone(false); setError(null); setOpen(true); }}
        style={linkStyle}
        title="Capture this turn as a golden-set item (eval ground truth)"
      >
        ＋ add to golden set{done ? " ✓" : ""}
      </button>
      <Modal open={open} onClose={close} title="Add to golden set">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Select
            label="Dataset"
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
          >
            <option value="">Select a dataset…</option>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, marginBottom: 4 }}>
              Expected answer (ground truth)
            </div>
            <TextArea
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              rows={5}
              placeholder="The correct answer this turn should have produced."
            />
          </div>
          {error && (
            <div style={{ color: COLORS.pink || "#dc2626", fontSize: 12 }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={save} loading={busy} disabled={busy}>Save</Button>
            <Button variant="secondary" onClick={close}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
